import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  MenuItem,
} from "@mui/material";

import {
  getAdminCustomers,
  setUserSuspended,
} from "../../api/adminApi";

const displayName = (row) => {
  const name = [row.first_name, row.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  return name || row.username || "-";
};

const displayLocation = (row) => {
  const location = [row.city, row.state, row.country]
    .filter(Boolean)
    .join(", ");

  return location || "-";
};

export default function AdminCustomers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await getAdminCustomers();
      setItems(res.data?.items || []);
    } catch (e) {
      console.error(e);

      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        `Failed to load customers (HTTP ${
          e?.response?.status || "?"
        })`;

      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visibleItems = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();

    return items.filter((row) => {
      if (statusFilter === "ACTIVE" && row.suspended) {
        return false;
      }

      if (statusFilter === "SUSPENDED" && !row.suspended) {
        return false;
      }

      if (!q) return true;

      const searchable = [
        row.customer_id,
        row.user_id,
        row.first_name,
        row.last_name,
        row.email,
        row.phone,
        row.username,
        row.customer_type,
        row.business_name,
        row.business_contact,
        row.city,
        row.state,
        row.country,
      ]
        .filter(
          (value) =>
            value !== null && value !== undefined
        )
        .join(" ")
        .toLowerCase();

      return searchable.includes(q);
    });
  }, [items, search, statusFilter]);

  const openSuspendDialog = (row) => {
    setTarget(row);
    setReason(row?.suspended_reason || "");
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setTarget(null);
    setReason("");
  };

  const confirmSuspendToggle = async () => {
    if (!target) return;

    setError("");

    try {
      const nextSuspended = !target.suspended;

      if (nextSuspended && !String(reason).trim()) {
        setError("Please enter a reason for suspension.");
        return;
      }

      await setUserSuspended(
        target.user_id,
        nextSuspended,
        String(reason || "").trim()
      );

      closeDialog();
      await load();
    } catch (e) {
      console.error(e);

      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        `Failed to update user status (HTTP ${
          e?.response?.status || "?"
        })`;

      setError(msg);
    }
  };

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, mb: 1 }}
      >
        Customers / Shippers
      </Typography>

      <Typography
        sx={{ mb: 2, color: "text.secondary" }}
      >
        Customer identity, business information,
        activity and account controls.
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: { xs: "stretch", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          mb: 2,
        }}
      >
        <TextField
          label="Search customers"
          placeholder="Name, email, phone, business..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { md: 320 } }}
        />

        <TextField
          select
          label="Status"
          size="small"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value)
          }
          sx={{ minWidth: { md: 160 } }}
        >
          <MenuItem value="ALL">All</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="SUSPENDED">
            Suspended
          </MenuItem>
        </TextField>

        <Button
          variant="outlined"
          onClick={load}
          disabled={loading}
        >
          Refresh
        </Button>

        <Typography sx={{ color: "text.secondary" }}>
          {visibleItems.length} shown
        </Typography>
      </Box>

      {loading && (
        <Typography
          sx={{ mb: 2, color: "text.secondary" }}
        >
          Loading…
        </Typography>
      )}

      {error && (
        <Typography sx={{ mb: 2, color: "error.main" }}>
          {error}
        </Typography>
      )}

      <Typography
         variant="caption"
         sx={{
           display: { xs: "block", md: "none" },
           mb: 1,
           color: "text.secondary",
         }}
      >
        Swipe table left/right to view all details and actions.
      </Typography>

      <TableContainer
         component={Paper}
         sx={{
            width: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            maxWidth: "100%",
            borderRadius: 2,
            "&::-webkit-scrollbar": {
               height: 8,
            },
          }}
      >
        <Table
          size="small"
          sx={{
            minWidth: {
              xs: 1050,
              md: 1050,
            },
          }}
        >
          <TableHead>
            <TableRow>
             <TableCell
               sx={{
                 position: "sticky",
                 left: 0,
                  zIndex: 3,
                  backgroundColor: "#f8fafc",
                  minWidth: 160,
                  borderRight: "1px solid #e5e7eb",
                }}
              >
                Name
              </TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>
                Account / Business
              </TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Activity</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleItems.map((row) => (
              <TableRow
                key={row.customer_id}
                hover
              >
                <TableCell
                  sx={{
                    minWidth: 160,
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    backgroundColor: "#fff",
                    borderRight: "1px solid #e5e7eb",
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    {displayName(row)}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.secondary",
                    }}
                  >
                    Customer #{row.customer_id} · User #
                    {row.user_id}
                  </Typography>
                </TableCell>

                <TableCell sx={{ minWidth: 220 }}>
                  <Typography variant="body2">
                    {row.email || "-"}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary" }}
                  >
                    {row.phone || "No phone"}
                  </Typography>

                  {row.username &&
                    row.username !== row.email && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "text.secondary",
                        }}
                      >
                        Username: {row.username}
                      </Typography>
                    )}
                </TableCell>

                <TableCell sx={{ minWidth: 190 }}>
                  <Typography
                    variant="body2"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {row.customer_type ||
                      "Not specified"}
                  </Typography>

                  {row.business_name && (
                    <Typography sx={{ fontWeight: 600 }}>
                      {row.business_name}
                    </Typography>
                  )}

                  {row.business_contact && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "text.secondary",
                      }}
                    >
                      Business contact:{" "}
                      {row.business_contact}
                    </Typography>
                  )}
                </TableCell>

                <TableCell sx={{ minWidth: 150 }}>
                  {displayLocation(row)}
                </TableCell>

                <TableCell sx={{ minWidth: 170 }}>
                  <Typography variant="body2">
                    {row.orders_count ?? 0} orders
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.secondary",
                    }}
                  >
                    Last:{" "}
                    {row.last_order_at
                      ? new Date(
                          row.last_order_at
                        ).toLocaleString()
                      : "No orders"}
                  </Typography>
                </TableCell>

                <TableCell sx={{ minWidth: 150 }}>
                  {row.created_at
                    ? new Date(
                        row.created_at
                      ).toLocaleString()
                    : "-"}
                </TableCell>

                <TableCell sx={{ minWidth: 170 }}>
                  {row.suspended ? (
                    <>
                      <Chip
                        label="SUSPENDED"
                        size="small"
                      />

                      {row.suspended_reason && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.5,
                            maxWidth: 220,
                            color: "text.secondary",
                          }}
                        >
                          {row.suspended_reason}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Chip
                      label="ACTIVE"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      openSuspendDialog(row)
                    }
                  >
                    {row.suspended
                      ? "Reactivate"
                      : "Suspend"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}

            {!visibleItems.length && !loading && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  sx={{ color: "text.secondary" }}
                >
                  No customers match the current
                  search/filter.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {target?.suspended
            ? "Reactivate Customer"
            : "Suspend Customer"}
        </DialogTitle>

        <DialogContent sx={{ mt: 1 }}>
          <Typography
            sx={{ mb: 0.5, fontWeight: 600 }}
          >
            {target
              ? displayName(target)
              : "User"}
          </Typography>

          <Typography
            sx={{ mb: 2, color: "text.secondary" }}
          >
            {target?.email || ""}
          </Typography>

          {!target?.suspended && (
            <TextField
              label="Suspension Reason (required)"
              value={reason}
              onChange={(e) =>
                setReason(e.target.value)
              }
              fullWidth
              multiline
              minRows={2}
            />
          )}

          {target?.suspended && (
            <Typography
              sx={{ color: "text.secondary" }}
            >
              This will reactivate the account.
              Previous reason:{" "}
              {target?.suspended_reason || "none"}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog}>
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={confirmSuspendToggle}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}